import React from "react";
import {
  FiHash, FiCalendar, FiCheckCircle, FiInfo,
  FiPhone, FiMail, FiMapPin, FiAlertCircle, FiClock,
} from "react-icons/fi";

/* ─────────────────────────────────────────────
   INLINE CSS
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

.it2__wrap * { box-sizing: border-box; margin: 0; padding: 0; }

.it2__wrap {
  font-family: 'DM Sans', sans-serif;
  background: #dde8e8;
  min-height: 100vh;
 
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

/* ── paper card — exact A4 ── */
.it2__card {
  width: 210mm;
  min-height: 297mm;
  background: #ffffff;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(10,85,86,0.18), 0 2px 8px rgba(10,85,86,0.10);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

/* ── top accent bar ── */
.it2__accent {
  height: 5px;
  background: linear-gradient(90deg, #0a5556 0%, #14b8a6 55%, #FFD166 100%);
}

/* ══════════════════════════════════════════════
   LAYOUT: main content (left) + sidebar (right)
══════════════════════════════════════════════ */
.it2__body {
  display: flex;
  flex: 1;
  min-height: 0;
}

/* ── main left panel ── */
.it2__main {
  flex: 1;
  padding: 36px 36px 36px 40px;
  border-right: 1px solid rgba(10,85,86,0.08);
  display: flex;
  flex-direction: column;
  gap: 28px;
}

/* ═════ HEADER ═════ */
.it2__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.it2__brand-row { display: flex; align-items: center; gap: 14px; }
.it2__brand-icon {
  width: 50px; height: 50px; border-radius: 15px; flex-shrink: 0;
  background: linear-gradient(135deg, #0a5556, #14b8a6);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 800; color: #fff;
  box-shadow: 0 6px 18px rgba(10,85,86,0.28);
}
.it2__company-name {
  font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 800;
  color: #0a5556; letter-spacing: -0.03em; margin-bottom: 4px;
}
.it2__seller-sub { font-size: 12px; color: #9ababa; font-weight: 500; }

/* status badge */
.it2__status {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 50px;
  font-size: 12px; font-family: 'Sora', sans-serif; font-weight: 700;
}
.it2__status--paid    { background: rgba(16,185,129,0.12); color: #065f46; border: 1px solid rgba(16,185,129,0.28); }
.it2__status--unpaid  { background: rgba(239,68,68,0.10); color: #991b1b; border: 1px solid rgba(239,68,68,0.26); }
.it2__status--partial { background: rgba(245,158,11,0.12); color: #92400e; border: 1px solid rgba(245,158,11,0.30); }
.it2__status--pending { background: rgba(59,130,246,0.10); color: #1d4ed8; border: 1px solid rgba(59,130,246,0.24); }
.it2__status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }

/* ═════ BILLING GRID ═════ */
.it2__billing {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.it2__bill-box {
  background: rgba(10,85,86,0.03);
  border: 1px solid rgba(10,85,86,0.09);
  border-radius: 14px; padding: 16px 18px;
}
.it2__box-lbl {
  font-size: 10px; font-family: 'Sora', sans-serif; font-weight: 700;
  letter-spacing: 0.09em; text-transform: uppercase; color: #9ababa; margin-bottom: 10px;
}
.it2__client-name {
  font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 800;
  color: #0f2a2a; margin-bottom: 8px;
}
.it2__client-row {
  display: flex; align-items: center; gap: 6px;
  font-size: 12.5px; color: #5a8080; margin-bottom: 5px;
}
.it2__client-row:last-child { margin-bottom: 0; }
.it2__client-row svg { color: #14b8a6; flex-shrink: 0; }

/* detail items box */
.it2__details-box {
  background: rgba(10,85,86,0.03);
  border: 1px solid rgba(10,85,86,0.09);
  border-radius: 14px; padding: 16px 18px;
  display: flex; flex-direction: column; gap: 12px;
}
.it2__detail-row {
  display: flex; align-items: center; gap: 10px;
}
.it2__detail-icon {
  width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
  background: rgba(10,85,86,0.09);
  display: flex; align-items: center; justify-content: center;
  color: #0a5556;
}
.it2__detail-lbl { font-size: 10.5px; font-family: 'Sora', sans-serif; font-weight: 600; color: #9ababa; text-transform: uppercase; letter-spacing: 0.06em; }
.it2__detail-val { font-size: 13px; font-family: 'Sora', sans-serif; font-weight: 700; color: #0f2a2a; }

/* ═════ ITEMS TABLE ═════ */
.it2__table-lbl {
  font-size: 10px; font-family: 'Sora', sans-serif; font-weight: 700;
  letter-spacing: 0.09em; text-transform: uppercase; color: #9ababa; margin-bottom: 12px;
}
.it2__table {
  width: 100%; border-collapse: collapse;
  border: 1px solid rgba(10,85,86,0.09); border-radius: 14px; overflow: hidden;
}
.it2__table thead tr {
  background: linear-gradient(90deg, rgba(10,85,86,0.07), rgba(20,184,166,0.04));
}
.it2__table thead th {
  font-family: 'Sora', sans-serif; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase; color: #0a5556;
  padding: 12px 16px; border-bottom: 1px solid rgba(10,85,86,0.09);
  white-space: nowrap;
}
.it2__table thead th:first-child { padding-left: 20px; }
.it2__table thead th.th-c { text-align: center; }
.it2__table thead th.th-r { text-align: right; padding-right: 20px; }

.it2__table tbody tr { border-bottom: 1px solid rgba(10,85,86,0.06); transition: background 0.12s; }
.it2__table tbody tr:last-child { border-bottom: none; }
.it2__table tbody tr:hover { background: rgba(10,85,86,0.025); }
.it2__table tbody td { padding: 13px 16px; font-size: 13px; color: #0f2a2a; vertical-align: middle; }
.it2__table tbody td:first-child { padding-left: 20px; }
.it2__table tbody td.td-c { text-align: center; font-family: 'Sora', sans-serif; font-weight: 700; }
.it2__table tbody td.td-r { text-align: right; padding-right: 20px; font-family: 'Sora', sans-serif; font-weight: 600; color: #5a8080; }
.it2__table tbody td.td-total { text-align: right; padding-right: 20px; }

.it2__item-name { font-size: 13px; font-weight: 700; color: #0f2a2a; margin-bottom: 4px; }
.it2__hsn-row   { display: flex; align-items: center; gap: 6px; }
.it2__hsn-chip {
  display: inline-block; padding: 2px 8px; border-radius: 6px;
  background: rgba(10,85,86,0.07); color: #5a8080;
  font-family: 'Sora', sans-serif; font-size: 10.5px; font-weight: 700;
}
.it2__tax-chip {
  display: inline-block; padding: 2px 8px; border-radius: 6px;
  background: rgba(20,184,166,0.10); color: #0c7172;
  font-family: 'Sora', sans-serif; font-size: 10.5px; font-weight: 700;
}
.it2__total-val { font-family: 'Sora', sans-serif; font-size: 13.5px; font-weight: 800; color: #0a5556; }

/* ═════ NOTES ═════ */
.it2__notes {
  background: rgba(10,85,86,0.03); border: 1px solid rgba(10,85,86,0.09);
  border-radius: 12px; padding: 14px 18px;
}
.it2__notes-hd {
  display: flex; align-items: center; gap: 6px;
  font-family: 'Sora', sans-serif; font-size: 11px; font-weight: 700;
  color: #0a5556; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 7px;
}
.it2__notes-hd svg { color: #14b8a6; }
.it2__notes-txt { font-size: 13px; color: #5a8080; line-height: 1.6; }

/* ══════════════════════════════════════════════
   SIDEBAR (right panel)
══════════════════════════════════════════════ */
.it2__sidebar {
  width: 240px; flex-shrink: 0;
  background: linear-gradient(170deg, #0a5556 0%, #0c7172 60%, #0d8080 100%);
  display: flex; flex-direction: column;
  justify-content: space-between;
}

.it2__sidebar-body { padding: 36px 26px 24px; }

.it2__summary-title {
  font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 800;
  color: rgba(255,255,255,0.55); text-transform: uppercase;
  letter-spacing: 0.1em; margin-bottom: 22px;
}
.it2__summary-list { display: flex; flex-direction: column; gap: 0; }
.it2__summary-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.it2__summary-row:last-of-type { border-bottom: none; }
.it2__sum-lbl { font-size: 12px; color: rgba(255,255,255,0.60); font-weight: 500; }
.it2__sum-val { font-family: 'Sora', sans-serif; font-size: 12.5px; font-weight: 700; color: rgba(255,255,255,0.85); }
.it2__sum-val--tax { color: rgba(255,209,102,0.80); }

/* grand total box */
.it2__grand-box {
  margin-top: 18px;
  background: rgba(255,255,255,0.10);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 14px; padding: 16px 18px;
}
.it2__grand-lbl {
  font-family: 'Sora', sans-serif; font-size: 10px; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.50); margin-bottom: 4px;
}
.it2__grand-val {
  font-family: 'Sora', sans-serif; font-size: 26px; font-weight: 800; color: #FFD166;
  line-height: 1; letter-spacing: -0.02em;
}

/* due date box */
.it2__due-box {
  margin-top: 16px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 12px; padding: 12px 16px;
  display: flex; align-items: center; gap: 10px;
}
.it2__due-icon {
  width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
  background: rgba(255,255,255,0.12);
  display: flex; align-items: center; justify-content: center;
  color: #FFD166;
}
.it2__due-lbl { font-size: 10px; font-family: 'Sora', sans-serif; font-weight: 600; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 2px; }
.it2__due-val { font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 700; color: #fff; }

/* sidebar footer */
.it2__sidebar-footer {
  padding: 20px 26px;
  border-top: 1px solid rgba(255,255,255,0.08);
  text-align: center;
}
.it2__thankyou {
  font-family: 'Sora', sans-serif; font-size: 11px; font-weight: 700;
  color: rgba(255,255,255,0.40); letter-spacing: 0.08em; text-transform: uppercase;
}

/* ── bottom strip ── */
.it2__bottom {
  height: 4px;
  background: linear-gradient(90deg, #FFD166 0%, #14b8a6 50%, #0a5556 100%);
}

/* ── print ── */
@page { size: A4 portrait; margin: 0; }
@media print {
  .it2__wrap { background: none !important; padding: 0 !important; display: block !important; }
  .it2__card {
    width: 210mm !important; min-height: 297mm !important;
    box-shadow: none !important; margin: 0 !important;
  }
}

@media (max-width: 680px) {
  .it2__body { flex-direction: column; }
  .it2__sidebar { width: 100%; }
  .it2__main { padding: 24px 20px; }
  .it2__billing { grid-template-columns: 1fr; }
  .it2__grand-val { font-size: 22px; }
}
`;

/* ─── helpers ─── */
const getStatusInfo = (status = "") => {
  const s = status.toLowerCase();
  if (s === "paid")    return { cls: "it2__status--paid",    dot: "#10b981" };
  if (s === "unpaid")  return { cls: "it2__status--unpaid",  dot: "#ef4444" };
  if (s === "partial") return { cls: "it2__status--partial", dot: "#f59e0b" };
  return                      { cls: "it2__status--pending", dot: "#3b82f6" };
};

const fmt  = (n) => `₹${Number(n || 0).toFixed(2)}`;
const fmtL = (n) => `₹${Number(n || 0).toLocaleString()}`;
const init = (name = "") => name.trim()[0]?.toUpperCase() || "?";

/* ═══════════════════════════════════════════════════
   COMPONENT — ALL DATA LOGIC COMPLETELY UNCHANGED
═══════════════════════════════════════════════════ */
const InvoiceTwo = ({ invoiceData }) => {
  console.log("invoiceData", invoiceData);

  /* ── DEFAULT FALLBACK DATA (UNCHANGED) ── */
  const defaultData = {
    invoiceNumber: "INV-002",
    invoiceDate: "2025-12-18",
    dueDate: "2026-01-02",
    paymentStatus: "Paid",
    seller: {
      name: "Your Company Name",
      gstin: "27AABCA5678A1Z5",
      phone: "9000000000",
      state: "Maharashtra",
    },
    customer: {
      name: "Acme Corp",
      email: "contact@acme.com",
      gstin: "27AABCA1234A1Z5",
      phone: "9876543210",
      address: "123 Main St, Maharashtra",
    },
    items: [
      { hsn: "1234", qty: 1, tax: 18, rate: 500,  description: "Product/Service A" },
      { hsn: "5678", qty: 1, tax: 12, rate: 1000, description: "Product/Service B" },
    ],
    gstBreakdown: {
      CGST: 105,
      SGST: 105,
    },
    grandTotal: 1710,
    itemSubtotal: 1500,
    notes: "Please make the payment within 15 days.",
  };

  /* ── FINAL DATA RESOLVER (UNCHANGED) ── */
  const data = invoiceData?.invoice_data
    ? {
        ...invoiceData.invoice_data,
        invoiceNumber: invoiceData.invoice_number,
        grandTotal: Number(invoiceData.grand_total),
      }
    : defaultData;

  const si = getStatusInfo(data.paymentStatus);

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <div className="it2__wrap">
        <div className="it2__card">

          {/* top gradient accent */}
          <div className="it2__accent" />

          <div className="it2__body">

            {/* ════ MAIN LEFT PANEL ════ */}
            <div className="it2__main">

              {/* Header */}
              <div className="it2__top">
                <div className="it2__brand-row">
                  <div className="it2__brand-icon">{init(data.seller?.name)}</div>
                  <div>
                    <div className="it2__company-name">{data.seller?.name}</div>
                    <div className="it2__seller-sub">
                      {data.seller?.gstin} &nbsp;·&nbsp; {data.seller?.phone}
                    </div>
                  </div>
                </div>
                <span className={`it2__status ${si.cls}`}>
                  <span className="it2__status-dot" style={{ background: si.dot }} />
                  {data.paymentStatus}
                </span>
              </div>

              {/* Billing Grid */}
              <div className="it2__billing">
                {/* Client box */}
                <div className="it2__bill-box">
                  <div className="it2__box-lbl">Client</div>
                  <div className="it2__client-name">{data.customer?.name}</div>
                  {data.customer?.address && (
                    <div className="it2__client-row"><FiMapPin size={12} /> {data.customer?.address}</div>
                  )}
                  {data.customer?.email && (
                    <div className="it2__client-row"><FiMail size={12} /> {data.customer?.email}</div>
                  )}
                  {data.customer?.phone && (
                    <div className="it2__client-row"><FiPhone size={12} /> {data.customer?.phone}</div>
                  )}
                </div>

                {/* Details box */}
                <div className="it2__details-box">
                  <div className="it2__box-lbl">Invoice Details</div>
                  <div className="it2__detail-row">
                    <div className="it2__detail-icon"><FiHash size={14} /></div>
                    <div>
                      <div className="it2__detail-lbl">Invoice No</div>
                      <div className="it2__detail-val">{data.invoiceNumber}</div>
                    </div>
                  </div>
                  <div className="it2__detail-row">
                    <div className="it2__detail-icon"><FiCalendar size={14} /></div>
                    <div>
                      <div className="it2__detail-lbl">Issue Date</div>
                      <div className="it2__detail-val">{data.invoiceDate}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div>
                <div className="it2__table-lbl">Line Items</div>
                <table className="it2__table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th className="th-c">Qty</th>
                      <th className="th-r">Price</th>
                      <th className="th-r">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items?.map((item, i) => (
                      <tr key={i}>
                        <td>
                          <div className="it2__item-name">{item.description}</div>
                          <div className="it2__hsn-row">
                            <span className="it2__hsn-chip">HSN: {item.hsn}</span>
                            <span className="it2__tax-chip">Tax: {item.tax}%</span>
                          </div>
                        </td>
                        <td className="td-c">{item.qty}</td>
                        <td className="td-r">{fmtL(item.rate)}</td>
                        <td className="td-total">
                          <span className="it2__total-val">
                            {fmtL(item.qty * item.rate)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Notes */}
              <div className="it2__notes">
                <div className="it2__notes-hd"><FiInfo size={13} /> Note</div>
                <div className="it2__notes-txt">{data.notes}</div>
              </div>

            </div>{/* end main */}

            {/* ════ SIDEBAR ════ */}
            <aside className="it2__sidebar">
              <div className="it2__sidebar-body">
                <div className="it2__summary-title">Summary</div>

                <div className="it2__summary-list">
                  <div className="it2__summary-row">
                    <span className="it2__sum-lbl">Subtotal</span>
                    <span className="it2__sum-val">{fmt(data.itemSubtotal)}</span>
                  </div>
                  <div className="it2__summary-row">
                    <span className="it2__sum-lbl">CGST</span>
                    <span className="it2__sum-val it2__sum-val--tax">{fmt(data.gstBreakdown?.CGST)}</span>
                  </div>
                  <div className="it2__summary-row">
                    <span className="it2__sum-lbl">SGST</span>
                    <span className="it2__sum-val it2__sum-val--tax">{fmt(data.gstBreakdown?.SGST)}</span>
                  </div>
                </div>

                {/* Grand Total */}
                <div className="it2__grand-box">
                  <div className="it2__grand-lbl">Amount Due</div>
                  <div className="it2__grand-val">{fmtL(data.grandTotal)}</div>
                </div>

                {/* Due Date */}
                <div className="it2__due-box">
                  <div className="it2__due-icon"><FiCalendar size={14} /></div>
                  <div>
                    <div className="it2__due-lbl">Due Date</div>
                    <div className="it2__due-val">{data.dueDate}</div>
                  </div>
                </div>
              </div>

              <div className="it2__sidebar-footer">
                <div className="it2__thankyou">Thank you for your business!</div>
              </div>
            </aside>

          </div>{/* end body */}

          {/* bottom strip */}
          <div className="it2__bottom" />

        </div>
      </div>
    </>
  );
};

export default InvoiceTwo;
